import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Shared behavior for the per-row "⋮ more actions" popover, used by
 * `OrderRowMenu`, `RfqRowMenu`, `RfqCard`, `PartDetailPage`, and (UID-421)
 * `PartRowActions`. The first four live inside an overflow container
 * (`overflow-x: auto` forces `overflow-y` to `auto`), so an in-flow absolute
 * panel is clipped at the card's edges — breaking the last row, the
 * second-to-last, and a single short row where neither direction fits.
 * `PartRowActions`' own `.parts-table-card` ancestor has the same exposure via
 * `overflow-y: hidden`. The panel is therefore PORTALED to <body> as
 * `position: fixed` (escaping the clip; bounded only by the viewport). This
 * hook owns open/close, outside-pointer + Escape dismissal, close-on-scroll/
 * resize (a fixed panel can't follow the trigger), and the up/down
 * positioning; each caller supplies only its own items.
 */

/** Approximate rendered panel height (3 rows + divider + padding), used only to
 * choose the open direction before the panel has measured itself. */
const MENU_APPROX_HEIGHT = 168
/** Gap between the trigger and the panel (matches the CSS `--space-1` offset). */
const MENU_GAP = 4

export interface MenuCoords {
  readonly left: number
  readonly top: number
  readonly dropUp: boolean
}

function computeCoords(trigger: HTMLElement): MenuCoords {
  const r = trigger.getBoundingClientRect()
  const spaceBelow = window.innerHeight - r.bottom
  const dropUp = spaceBelow < MENU_APPROX_HEIGHT + MENU_GAP && r.top > spaceBelow
  return {
    left: r.right, // right edge; the panel is shifted left by its own width via transform
    top: dropUp ? r.top - MENU_GAP : r.bottom + MENU_GAP,
    dropUp,
  }
}

export function useRowActionsMenu() {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<MenuCoords | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  function toggleOpen() {
    setOpen((current) => {
      const next = !current
      if (next && triggerRef.current) {
        setCoords(computeCoords(triggerRef.current))
      }
      return next
    })
  }

  // Once the panel has rendered, re-measure a drop-up panel with its ACTUAL
  // height so it sits flush above the trigger (the approx-height guess only picks
  // the direction). Runs before paint to avoid a visible jump.
  useLayoutEffect(() => {
    if (!open || coords === null || !coords.dropUp || !panelRef.current || !triggerRef.current) {
      return
    }
    const panelHeight = panelRef.current.getBoundingClientRect().height
    const triggerTop = triggerRef.current.getBoundingClientRect().top
    const desiredTop = triggerTop - MENU_GAP - panelHeight
    if (Math.abs(desiredTop - coords.top) > 0.5) {
      setCoords((c) => (c ? { ...c, top: desiredTop, dropUp: false } : c))
    }
  }, [open, coords])

  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node
      // The panel is portaled OUT of `containerRef`, so check it separately —
      // otherwise a click on a menu item would count as "outside" and close the
      // menu before the item's handler runs.
      if (!containerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // A fixed panel does not follow the trigger when an ancestor scrolls or the
    // window resizes, so close rather than leave it detached. Capture phase
    // catches the recent-activity card's own horizontal scroll too.
    function handleReflow() {
      setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleReflow, true)
    window.addEventListener('resize', handleReflow)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleReflow, true)
      window.removeEventListener('resize', handleReflow)
    }
  }, [open])

  /** Wrap an action so clicking a menu item stops propagation, closes the menu,
   * then runs the action (mirrors the prior per-component `closeAnd`). */
  function runAndClose(action: () => void) {
    return (event: { stopPropagation: () => void }) => {
      event.stopPropagation()
      setOpen(false)
      action()
    }
  }

  return { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose }
}

/** Renders the popover panel to <body> (fixed) with the computed position.
 * Returns null while closed. `panelRef`/`menuId`/`coords` come from the hook. */
export function RowActionsMenuPortal({
  open,
  coords,
  menuId,
  panelRef,
  role,
  onKeyDown,
  children,
}: {
  readonly open: boolean
  readonly coords: MenuCoords | null
  readonly menuId: string
  readonly panelRef: React.RefObject<HTMLDivElement | null>
  /** UID-421 — `PartRowActions` is an ARIA menu (role="menu" + per-item
   * role="menuitem"), unlike this hook's other callers, which are plain
   * button popovers with no role. Optional so those are unaffected. */
  readonly role?: string | undefined
  /** UID-421 — an ARIA-menu caller's own roving-focus (Up/Down/Home/End)
   * handler, since portaling to <body> takes the panel out of the trigger's
   * DOM subtree, breaking native Tab order into it (see `PartRowActions`'
   * doc comment). Optional; other callers don't need roving focus. */
  readonly onKeyDown?: ((event: React.KeyboardEvent<HTMLDivElement>) => void) | undefined
  readonly children: ReactNode
}) {
  if (!open || coords === null) return null
  return createPortal(
    <div
      id={menuId}
      ref={panelRef}
      role={role}
      onKeyDown={onKeyDown}
      className="row-actions-menu-panel row-actions-menu-panel--fixed"
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        transform: coords.dropUp ? 'translate(-100%, -100%)' : 'translateX(-100%)',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
